import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const MUSTANG_URL =
  "https://images.unsplash.com/photo-1575844611398-2a68400b437c?q=80&w=2070&auto=format&fit=crop";

export function HeroKinetic() {
  const sectionRef = useRef<HTMLElement>(null);
  const stripeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });
      tl.from(stripeRef.current, { xPercent: -120, duration: 1.5 })
        .from(".hk-headline", { opacity: 0, x: -50, duration: 1, ease: "power4.out" }, "-=0.5")
        .from(".hk-mission", { opacity: 0, y: 16, duration: 0.8 }, "-=0.6");
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-black"
      id="hero"
      /* No overflow:hidden here — that was clipping the stripe.
         The image container below handles its own clipping. */
      style={{ height: "100vh", minHeight: "100svh" }}
    >
      {/* Image — overflow clipped on this wrapper only */}
      <div className="absolute inset-0" style={{ overflow: "hidden" }}>
        <img
          src={MUSTANG_URL}
          alt=""
          aria-hidden
          className="w-full h-full object-cover"
          style={{
            objectPosition: "center 75%",
            filter: "grayscale(100%) contrast(1.2)",
            opacity: 0.6,
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/45" />
        {/* Top scrim — ensures logo/tagline text is always readable */}
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: "35%", background: "linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)" }}
        />
      </div>

      {/* Logo + tagline — top center, below preview banner */}
      <div
        className="hk-mission absolute z-30 flex flex-col items-center text-center"
        style={{ top: "6%", left: 0, right: 0 }}
      >
        <img
          src="/logo-clean.webp"
          alt="Bizzy Professional Details"
          style={{ height: 160, width: "auto", marginBottom: 20, objectFit: "contain", filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.6))" }}
        />
        {/* Line 1 — display headline */}
        <p
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 900,
            fontStyle: "italic",
            fontSize: "clamp(1.1rem, 2.2vw, 1.6rem)",
            textTransform: "uppercase",
            letterSpacing: "-0.01em",
            lineHeight: 1.1,
            color: "#fff",
            marginBottom: 8,
            textShadow: "0 2px 12px rgba(0,0,0,0.9)",
          }}
        >
          Where <span style={{ color: "#1E5EFF" }}>Showroom Shine</span> Meets Real Life Mess.
        </p>
        {/* Line 2 — mono subtext */}
        <p
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.62rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#fff",
            textShadow: "0 1px 8px rgba(0,0,0,1)",
            lineHeight: 1.6,
          }}
        >
          Clean Cars Drive Better —{" "}
          <span style={{ color: "#fff", fontStyle: "italic" }}>
            Scientifically unproven, but you'll agree.
          </span>
        </p>
      </div>

      {/* Black mask below the stripe — hides image beneath it */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "14%",
          background: "#000",
          zIndex: 19,
        }}
      />

      {/* Diagonal stripe — sits above the black mask */}
      <div
        ref={stripeRef}
        style={{
          position: "absolute",
          bottom: "8%",
          left: "-10%",
          width: "120%",
          height: 160,
          background: "#1E5EFF",
          transform: "rotate(-6deg)",
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          paddingLeft: "12%",
          boxShadow: "0 0 80px rgba(30,94,255,0.3)",
        }}
      >
        <h1
          className="hk-headline"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 900,
            fontStyle: "italic",
            fontSize: "clamp(3rem, 8vw, 8rem)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
            color: "#fff",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          FULL SPEED{" "}
          <span style={{ color: "rgba(0,0,0,0.6)" }}>PRECISION</span>
        </h1>
      </div>
    </section>
  );
}
